import {
  moneyFormatter,
  percentFormatter,
  SmallBusinessRetirementTracFormatter,
  workplaceRetirementFormatter
} from 'services/TableUtils';

import { dailyChangeRate } from 'services/Calculation';

/*
https://www.ag-grid.com/javascript-grid-column-properties/
sortable: allows sorting on each column
suppressMenu: suppresses built-in filtering/selection menu
animateRows: animates rows upon data, sort, or filter change
aggFunc: column aggregates default to sum of the column
minWidth: minimum width for each column
 */

export const defaultColumnDef = {
  sortable: true,
  suppressMenu: true,
  animateRows: true,
  suppressMovable: true,
  aggFunc: 'sum',
  minWidth: 170,
  autoHeight: true,
  headerClass: 'ag-right-aligned-header'
};

/*
https://www.ag-grid.com/javascript-grid-column-properties/
headerName: empty string removes name from column header
field: value upon which the group column aggregates data (in this case, the account's name and checksum)
pinned: pins column to left for constant viewing
wrapText: wraps column text to prevent column from growing from a single line of text
autoHeight: automatically sets minimum row height based on this column's data
min/max/width: sets width of column
cellClass: sets css class applied to this particular column
tooltipField: sets data to be displayed in hoverable tooltip
sort: default sort for column (descending alphabetical in this case)
rowGroupIndex: sets which layer of group this column corresponds to (in this case, 4 is the lowest grouping.)
cellRendererParams: parameters sent to cell renderer, modifying what is displayed in the column's cells
  suppressCount: suppresses display of a count of how many children belong to each group
  suppressPadding: suppresses automatic lefthand padding on each level of group (i.e. without this lower groups would have
                   crazy indents)
  innerRenderer: sets cell renderer to be used
 */

export const autoGroupColumnDef = {
  headerName: '',
  field: 'nameAndChecksum',
  pinned: 'left',
  minWidth: 50,
  maxWidth: 350,
  width: 350,
  cellClass: 'pinned-cell-align lock-pinned',
  sort: null,
  rowGroupIndex: 1,
  suppressSizeToFit: true,
  autoHeight: true,
  resizable: true,
  cellRendererParams: {
    suppressCount: true,
    suppressDoubleClickExpand: true,
    suppressEnterExpand: true,
    suppressPadding: true,
    innerRenderer: 'accountNameRenderer'
  }
};

export const autoGroupColumnDefMobile = {
  headerName: '',
  field: 'nameAndChecksum',
  wrapText: true,
  autoHeight: true,
  minWidth: 224,
  maxWidth: 224,
  width: 224,
  suppressSizeToFit: true,
  cellClass: 'pinned-cell-align lock-pinned',
  sort: null,
  rowGroupIndex: 1,
  cellRendererParams: {
    suppressCount: true,
    suppressDoubleClickExpand: true,
    suppressEnterExpand: true,
    suppressPadding: true,
    innerRenderer: 'accountNameRenderer'
  }
};

export function ColumnDefFactory() {
  this.createColumnDef = (key, accounts) => {
    let def;

    if (isRowGroup(key)) {
      def = new RowGroupCol(key);
    } else if (isColHidden(key)) {
      def = new HiddenCol();
    } else if (key === 'priceChangePercentSum') {
      def = new PriceChangePercentCol(accounts);
    } else if (key === 'difference') {
      def = new DailyChangeCol();
    } else {
      def = new RegCol();
    }

    def.field = key;

    return def;
  };
}

const isRowGroup = (key) => {
  return key === 'workplaceRetirement' || key === 'tracRetailRetirement';
};

export const visibleColumns = [
  'balance',
  'difference',
  'priceChangePercentSum'
];

const isColHidden = (key) => !visibleColumns.includes(key);

const rowGroupOrder = {
  workplaceRetirement: 0
};

const RowGroupCol = function (key) {
  this.headerName = '';
  this.rowGroup = true;
  this.hide = true;
  this.cellClass = 'pinned-cell-align';
  this.rowGroupIndex = rowGroupOrder[key];

  if (key === 'workplaceRetirement') {
    this.valueFormatter = workplaceRetirementFormatter;
  }
  if(key === 'tracRetailRetirement'){
    this.valueFormatter = SmallBusinessRetirementTracFormatter;
  }
};

const HiddenCol = function () {
  this.hide = true;
};

const PriceChangePercentCol = function (accounts) {
  this.headerName = 'Daily Change (%)';
  this.valueFormatter = percentFormatter;
  this.aggFunc = () => {
    return dailyChangeRate(accounts);
  };
};

const DailyChangeCol = function () {
  this.headerName = 'Daily Change ($)';
  this.valueFormatter = moneyFormatter;
};

const RegCol = function () {
  this.valueFormatter = moneyFormatter;
};
